import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { sendOtp, verifyOtp, formatApiErrorDetail } from "@/lib/api";

const CODES = ["+91", "+1", "+44", "+971", "+65", "+61", "+49", "+27"];

const FIELD =
  "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[15px] text-[#111111] placeholder:text-gray-400 focus:outline-none focus:border-[#0B63F6] focus:ring-1 focus:ring-[#0B63F6] transition-colors";

// Controlled phone field with WhatsApp OTP verification.
// Props: countryCode, phone, onCountryCode, onPhone, token, onToken(token|null)
export default function PhoneOtpField({ countryCode, phone, onCountryCode, onPhone, token, onToken }) {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const verified = !!token;

  const resetVerification = () => {
    if (token) onToken(null);
    setOtpSent(false);
    setOtp("");
  };

  const handleSend = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 6) return toast.error("Enter a valid WhatsApp number.");
    setSending(true);
    try {
      await sendOtp(countryCode, phone);
      setOtpSent(true);
      toast.success("OTP sent on WhatsApp.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send OTP.");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.length < 4) return toast.error("Enter the OTP you received.");
    setVerifying(true);
    try {
      const res = await verifyOtp(countryCode, phone, otp);
      onToken(res.otp_token);
      toast.success("Phone verified.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid OTP.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="phone-otp-field">
      <div className="flex gap-2">
        <select
          value={countryCode}
          onChange={(e) => { onCountryCode(e.target.value); resetVerification(); }}
          data-testid="otp-country-code"
          className="bg-white border border-gray-200 rounded-xl px-2 py-3 text-[15px] focus:outline-none focus:border-[#0B63F6] w-[84px]"
          disabled={verified}
        >
          {CODES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          className={`${FIELD} flex-1 ${verified ? "border-[#19A974] bg-[#19A974]/[0.04]" : ""}`}
          placeholder="WhatsApp number*"
          value={phone}
          onChange={(e) => { onPhone(e.target.value); resetVerification(); }}
          data-testid="otp-phone"
          disabled={verified}
          inputMode="tel"
        />
        {verified ? (
          <span className="inline-flex items-center gap-1 rounded-xl bg-[#19A974]/10 text-[#19A974] px-3 text-sm font-medium shrink-0" data-testid="otp-verified-badge">
            <CheckCircle2 className="h-4 w-4" /> Verified
          </span>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            data-testid="otp-send-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#111111] text-white px-4 text-sm font-medium hover:bg-[#0B63F6] transition-colors disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {otpSent ? "Resend" : "Send OTP"}
          </button>
        )}
      </div>

      {otpSent && !verified && (
        <div className="flex gap-2">
          <input
            className={FIELD}
            placeholder="Enter WhatsApp OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="otp-input"
            inputMode="numeric"
          />
          <button
            type="button"
            onClick={handleVerify}
            disabled={verifying}
            data-testid="otp-verify-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#0B63F6] text-white px-5 text-sm font-medium hover:bg-[#6E56CF] transition-colors disabled:opacity-60"
          >
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
          </button>
        </div>
      )}
    </div>
  );
}
